/**
 * Per-category accent for the folder cards and the category pill.
 *
 * WHY COLOUR AT ALL, AFTER S5 SAID NO. `CategoryPill`'s own comment reads
 * "Deliberately NOT eleven colours. Colour-coding the taxonomy is a design
 * decision with an accessibility bill attached, and it is not this slice's."
 * That was true of S5. The bill is paid here: every surface that carries an
 * accent also carries the folder's NAME, in full, in text — the colour is a
 * second, redundant channel and never the only one. Remove every hue from
 * this file and the page still tells a reader exactly which folder is which.
 *
 * WHY ONLY THE GLYPH AND THE BORDER, AND NEVER THE SURFACE. The S2 theme
 * guard's T6 requires every `bg-*` on this feature to be a semantic token,
 * with no exception for a `dark:` pair — a painted surface is what decides
 * whether Light Mode is readable at all. So the tinted squares from the
 * reference are built the other way round here: a token surface
 * (`bg-muted`), a palette-coloured icon, and a palette-coloured hairline.
 * Close to the reference to look at, and still a surface the theme owns.
 *
 * EVERY PALETTE CLASS SHIPS ITS `dark:` TWIN, because a fixed palette colour
 * is theme-blind on its own — `text-sky-600` is legible on white and muddy
 * on the dark card. Guard T7 in `AdminDocumentsPage.theme.test.js` checks
 * that pairing on the strings below, since they never appear as a literal
 * `className` attribute the other guards could see.
 *
 * AN UNKNOWN CATEGORY IS NOT AN ERROR. A legacy row can carry a key outside
 * the whitelist (see `summarize_documents_by_category`'s "an unknown
 * category is counted, not dropped"). It gets the neutral accent and its
 * raw value as a label, exactly as before.
 */

/** The neutral accent: also what an unknown category gets. */
export const NEUTRAL_ACCENT = Object.freeze({
  icon: "text-muted-foreground",
  border: "border-border",
});

/**
 * `""` is the all-documents pseudo-folder the page has always used for "no
 * category filter", so it keeps that vocabulary here and takes the theme's
 * own primary rather than a hue of its own.
 */
const ALL_ACCENT = Object.freeze({
  icon: "text-primary",
  border: "border-primary/30",
});

const BY_CATEGORY = Object.freeze({
  mietvertraege: { icon: "text-sky-600 dark:text-sky-400", border: "border-sky-500/30 dark:border-sky-400/30" },
  bewerbungs_dokumente: { icon: "text-violet-600 dark:text-violet-400", border: "border-violet-500/30 dark:border-violet-400/30" },
  hausordnung_sops: { icon: "text-emerald-600 dark:text-emerald-400", border: "border-emerald-500/30 dark:border-emerald-400/30" },
  eigentuemer_vertraege: { icon: "text-amber-600 dark:text-amber-400", border: "border-amber-500/30 dark:border-amber-400/30" },
  rechnungen: { icon: "text-rose-600 dark:text-rose-400", border: "border-rose-500/30 dark:border-rose-400/30" },
  interne_dokumente: { icon: "text-cyan-600 dark:text-cyan-400", border: "border-cyan-500/30 dark:border-cyan-400/30" },
  marketing_kommunikation: { icon: "text-fuchsia-600 dark:text-fuchsia-400", border: "border-fuchsia-500/30 dark:border-fuchsia-400/30" },
  behoerden_recht: { icon: "text-orange-600 dark:text-orange-400", border: "border-orange-500/30 dark:border-orange-400/30" },
  ai_cto_reports: { icon: "text-indigo-600 dark:text-indigo-400", border: "border-indigo-500/30 dark:border-indigo-400/30" },
  konkurrenzanalysen: { icon: "text-teal-600 dark:text-teal-400", border: "border-teal-500/30 dark:border-teal-400/30" },
  sonstiges: { icon: "text-slate-600 dark:text-slate-400", border: "border-slate-500/30 dark:border-slate-400/30" },
});

/** Every accent string in this module, for the theme guard to check. */
export const ALL_ACCENT_CLASS_STRINGS = Object.freeze([
  ...Object.values(BY_CATEGORY).flatMap((a) => [a.icon, a.border]),
  ALL_ACCENT.icon,
  ALL_ACCENT.border,
  NEUTRAL_ACCENT.icon,
  NEUTRAL_ACCENT.border,
]);

export function categoryAccent(value) {
  if (value === "") return ALL_ACCENT;
  return BY_CATEGORY[value] || NEUTRAL_ACCENT;
}
